import { useAuth } from '@/contexts/AuthContext';
import {
  canAccessPath as canAccessPathFor,
  evaluateAccessRule,
  hasModuleAction,
  type AccessRule,
  type ModulePermissions,
} from '@/lib/route-access';

export const usePermissions = () => {
  const { state, hasPermission, hasAnyPermission } = useAuth();

  // `state.user.permissions` vem tipado no formato legado (system/company/custom),
  // mas o servidor manda as permissões do nível: { [módulo]: { actions, scope } }.
  const modulePermissions = state.user?.permissions as unknown as ModulePermissions | undefined;

  /** Permissão no nível de módulo. Mesma regra da trava de rotas e dos menus. */
  const hasModulePermission = (module: string, action: string = 'read'): boolean =>
    hasModuleAction(modulePermissions, module, action);

  /** A pessoa pode abrir este endereço? Fonte: `lib/route-access.ts`. */
  const canAccessPath = (href: string): boolean => canAccessPathFor(modulePermissions, href);

  const can = (rule: AccessRule): boolean => evaluateAccessRule(modulePermissions, rule);

  const isAdmin = (): boolean => {
    return hasPermission('admin') || hasAnyPermission(['system.admin', 'company.admin']);
  };

  const canManageUsers = (): boolean => {
    return hasModulePermission('users', 'read');
  };

  const canManageCompany = (): boolean => {
    return hasModulePermission('company', 'read');
  };

  const canViewReports = (): boolean => {
    return hasModulePermission('reports', 'read');
  };

  const canManageIntegrations = (): boolean => {
    return hasModulePermission('integrations', 'read');
  };

  const canManageChannels = (): boolean => {
    return hasModulePermission('channels', 'read');
  };

  const canManageTickets = (): boolean => {
    return hasModulePermission('tickets', 'read');
  };

  const canManageLeads = (): boolean => {
    return hasModulePermission('crm', 'read');
  };

  const canManageContacts = (): boolean => {
    return hasModulePermission('contacts', 'read');
  };

  const canManageProducts = (): boolean => {
    return hasModulePermission('products', 'read');
  };

  const canViewDashboard = (): boolean => {
    // Allow any authenticated user to view the dashboard
    return state.isAuthenticated;
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasModulePermission,
    canAccessPath,
    can,
    isAdmin,
    canManageUsers,
    canManageCompany,
    canViewReports,
    canManageIntegrations,
    canManageChannels,
    canManageTickets,
    canManageLeads,
    canManageContacts,
    canManageProducts,
    canViewDashboard,
  };
};